const FINGERPRINT_VERSION = 2;
const FINGERPRINT_CACHE_KEY = "cekresi_tracking_fingerprint_v2";
const FINGERPRINT_CACHE_TTL_MS = 30 * 60 * 1000;
const FINGERPRINT_HASH_PATTERN = /^[a-f0-9]{16,128}$/;
const FINGERPRINT_PAYLOAD_MAX_LENGTH = 1200;

let inMemoryFingerprint = null;
let inFlightFingerprint = null;
let fingerprintJsLoader = null;

function getFingerprintJsLoader() {
  if (!fingerprintJsLoader) {
    fingerprintJsLoader = import("@fingerprintjs/fingerprintjs").then((mod) => mod.default.load());
  }
  return fingerprintJsLoader;
}

function getNavigator() {
  if (typeof navigator === "undefined") {
    return null;
  }
  return navigator;
}

function getWindow() {
  if (typeof window === "undefined") {
    return null;
  }
  return window;
}

function stableStringify(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return JSON.stringify(value);
  }
  const sorted = {};
  Object.keys(value)
    .sort()
    .forEach((key) => {
      sorted[key] = value[key];
    });
  return JSON.stringify(sorted);
}

function isValidFingerprintHash(value) {
  return typeof value === "string" && FINGERPRINT_HASH_PATTERN.test(value);
}

function simpleHash(value) {
  let hash = 2166136261;
  let secondHash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    hash ^= code;
    hash = Math.imul(hash, 16777619);
    secondHash = Math.imul(secondHash ^ (code + index), 16777619);
  }
  return `${(hash >>> 0).toString(16).padStart(8, "0")}${(secondHash >>> 0).toString(16).padStart(8, "0")}`;
}

async function sha256(value) {
  const win = getWindow();
  if (!win?.crypto?.subtle || typeof TextEncoder === "undefined") {
    return simpleHash(value);
  }
  const encoded = new TextEncoder().encode(value);
  const digest = await win.crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function readCachedFingerprint() {
  const win = getWindow();
  if (!win) {
    return null;
  }
  try {
    const raw = win.localStorage.getItem(FINGERPRINT_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (
      parsed?.version === FINGERPRINT_VERSION &&
      typeof parsed.hash === "string" &&
      typeof parsed.payload === "string" &&
      Date.now() - Number(parsed.createdAt || 0) < FINGERPRINT_CACHE_TTL_MS
    ) {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
}

function writeCachedFingerprint(fingerprint) {
  const win = getWindow();
  if (!win || !fingerprint) {
    return;
  }
  try {
    win.localStorage.setItem(FINGERPRINT_CACHE_KEY, JSON.stringify(fingerprint));
  } catch {
    // Fingerprint collection is best-effort only.
  }
}

function bucketDimension(value, size = 100) {
  const numeric = Number(value) || 0;
  if (numeric <= 0) {
    return 0;
  }
  return Math.round(numeric / size) * size;
}

function collectPayload() {
  const nav = getNavigator();
  const win = getWindow();
  if (!nav || !win) {
    return null;
  }

  const screen = win.screen || {};
  const screenWidth = Number(screen.width) || 0;
  const screenHeight = Number(screen.height) || 0;
  const viewportWidth = Number(win.innerWidth) || 0;
  const viewportHeight = Number(win.innerHeight) || 0;
  return {
    v: FINGERPRINT_VERSION,
    tz: Intl.DateTimeFormat?.().resolvedOptions?.().timeZone || "",
    tzOffset: new Date().getTimezoneOffset(),
    lang: nav.language || "",
    platform: nav.platform || "",
    screenClass: `${Math.min(screenWidth, screenHeight)}x${Math.max(screenWidth, screenHeight)}x${Number(screen.colorDepth) || 0}`,
    viewportClass: `${bucketDimension(Math.min(viewportWidth, viewportHeight))}x${bucketDimension(Math.max(viewportWidth, viewportHeight))}`,
    pixelRatio: Number(win.devicePixelRatio) || 0,
    cores: Number(nav.hardwareConcurrency) || 0,
    memory: Number(nav.deviceMemory) || 0,
    touch: Number(nav.maxTouchPoints) || 0,
    cookies: Boolean(nav.cookieEnabled),
    dnt: nav.doNotTrack || win.doNotTrack || "",
    webdriver: Boolean(nav.webdriver),
    mobile: Boolean(nav.userAgentData?.mobile),
    uaPlatform: nav.userAgentData?.platform || ""
  };
}



function compactPayload(payload) {
  let serialized = JSON.stringify(payload);
  if (serialized.length <= FINGERPRINT_PAYLOAD_MAX_LENGTH) {
    return serialized;
  }

  const compacted = {
    ...payload,
    components: payload.components?.slice(0, 16)
  };
  serialized = JSON.stringify(compacted);
  if (serialized.length <= FINGERPRINT_PAYLOAD_MAX_LENGTH) {
    return serialized;
  }

  return JSON.stringify({
    v: payload.v,
    provider: payload.provider,
    visitorId: payload.visitorId,
    confidence: payload.confidence,
    componentCount: payload.componentCount
  });
}

async function buildFingerprintJsFingerprint(telemetry) {
  const agent = await getFingerprintJsLoader();
  const result = await agent.get();
  const visitorId = String(result?.visitorId || "").toLowerCase();
  if (!isValidFingerprintHash(visitorId)) {
    return null;
  }

  const components = result?.components && typeof result.components === "object"
    ? Object.keys(result.components).sort()
    : [];
  const payload = compactPayload({
    v: FINGERPRINT_VERSION,
    provider: "fingerprintjs",
    visitorId,
    confidence: typeof result?.confidence?.score === "number" ? result.confidence.score : null,
    components,
    componentCount: components.length,
    telemetry
  });

  return {
    version: FINGERPRINT_VERSION,
    hash: visitorId,
    payload,
    createdAt: Date.now()
  };
}

async function buildFallbackFingerprint(payload) {
  const stablePayload = { ...payload };
  delete stablePayload.screen;
  delete stablePayload.viewport;
  const hash = await sha256(stableStringify(stablePayload));
  const fallbackPayload = compactPayload({
    v: FINGERPRINT_VERSION,
    provider: "custom_fallback",
    telemetry: payload
  });

  return {
    version: FINGERPRINT_VERSION,
    hash,
    payload: fallbackPayload,
    createdAt: Date.now()
  };
}

export async function getTrackingFingerprint() {
  if (inMemoryFingerprint) {
    return inMemoryFingerprint;
  }

  const cached = readCachedFingerprint();
  if (cached) {
    inMemoryFingerprint = cached;
    return cached;
  }

  if (!inFlightFingerprint) {
    inFlightFingerprint = (async () => {
      const payload = collectPayload();
      if (!payload) {
        return null;
      }

      let fingerprint = null;
      try {
        fingerprint = await buildFingerprintJsFingerprint(payload);
      } catch {
        fingerprint = null;
      }
      fingerprint = fingerprint || await buildFallbackFingerprint(payload);
      inMemoryFingerprint = fingerprint;
      writeCachedFingerprint(fingerprint);
      return fingerprint;
    })().finally(() => {
      inFlightFingerprint = null;
    });
  }

  try {
    return await inFlightFingerprint;
  } catch {
    return null;
  }
}

export async function getTrackingFingerprintHeaders() {
  const fingerprint = await getTrackingFingerprint();
  if (!fingerprint?.hash || !fingerprint?.payload) {
    return {};
  }
  return {
    "X-Tracking-Fingerprint-Hash": fingerprint.hash,
    "X-Tracking-Fingerprint-Payload": fingerprint.payload,
    "X-Tracking-Fingerprint-Version": String(fingerprint.version)
  };
}
